/**
 * Auth Slice for LifeScribe Admin Dashboard
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, AdminUser, AddUserRequest } from '../types/auth';
import { PERMISSION_RESOURCES } from '../config/permissionResources';
import apiService from '../services/api';
import { clearOverviewCache } from '../services/overviewPrefetch';

// ═══════════════════════════════════════════════════════════════════════════
// LOCAL DEV ONLY — fake admin session
// ═══════════════════════════════════════════════════════════════════════════
// `__DEV_FAKE_ADMIN__` is injected by webpack's DefinePlugin. In PRODUCTION
// builds it is the literal `false`, so everything below collapses to
// `if (false) { … }` and is removed by the minifier — the fake email never
// exists in a shipped bundle. In development it is only true when the dev
// server is started with LSADMIN_DEV_FAKE_ADMIN=1.
//
// Purpose: drive the dashboard locally (UI work, screenshots) without a real
// Google OAuth session against the production admin API. It seeds a super_admin
// with every permission and SKIPS the /auth/verify network call entirely.
// NEVER reference this flag for anything a real user's access depends on.
declare const __DEV_FAKE_ADMIN__: boolean;

// Guarded so jest (which does not run DefinePlugin) never hits a ReferenceError;
// jest.config.js also defines the global as false.
const DEV_FAKE_ADMIN_ENABLED =
  typeof __DEV_FAKE_ADMIN__ !== 'undefined' && __DEV_FAKE_ADMIN__ === true;

function buildFakeDevAdmin(): AdminUser {
  const now = new Date().toISOString();
  return {
    id: '00000000-0000-4000-8000-000000000000',
    oauth_provider: 'google',
    oauth_user_id: 'dev-local',
    // .invalid is a reserved TLD — this address can never resolve anywhere.
    email: 'dev-local@lifescribe.invalid',
    name: 'Local Dev Admin',
    permissions: PERMISSION_RESOURCES.map((r) => ({
      resource: r.id,
      access_level: 'full' as const,
    })),
    role: 'super_admin',
    legacy_role: 'super_admin',
    is_active: true,
    created_at: now,
    updated_at: now,
  };
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  permissions: [],
};

// Async thunks
export const verifyAuth = createAsyncThunk(
  'auth/verify',
  async (_, { rejectWithValue }) => {
    // LOCAL DEV ONLY — see the comment block at the top of this file. Dead code
    // in production builds (`__DEV_FAKE_ADMIN__` is the literal false there).
    if (DEV_FAKE_ADMIN_ENABLED) {
      // eslint-disable-next-line no-console
      console.warn(
        '⚠️ [AUTH_SLICE] LSADMIN_DEV_FAKE_ADMIN is on — using a FAKE local super_admin ' +
          'session and skipping the /auth/verify check. Local development only.'
      );
      return { authenticated: true, user: buildFakeDevAdmin() };
    }

    try {
      const result = await apiService.verifyAuth();
      console.log('🔐 Auth verification result:', result);
      return result;
    } catch (error) {
      // Don't treat authentication verification failure as an error
      // Just return unauthenticated state to prevent infinite loops
      console.log('🔐 Auth verification failed - user not authenticated (this is normal after logout)');
      
      // Clear any potentially invalid tokens from local storage/session storage
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (cleanupError) {
        console.warn('Failed to clear local storage during auth verification failure:', cleanupError);
      }
      
      // Return successful response with unauthenticated state instead of rejecting
      return { authenticated: false, user: null };
    }
  }
);

export const getUserProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const user = await apiService.getUserProfile();
      return user;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to get profile');
    }
  }
);

export const addUser = createAsyncThunk(
  'auth/addUser',
  async (userData: AddUserRequest, { rejectWithValue }) => {
    try {
      const response = await apiService.addUser(userData);
      if (!response.success) {
        throw new Error(response.message || 'Failed to add user');
      }
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add user');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: any, { rejectWithValue }) => {
    try {
      const response = await apiService.updateAdminProfile(profileData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    // First, before anything can fail: the next person to sign in on this
    // browser must not be handed this user's prefetched Overview numbers.
    clearOverviewCache();
    try {
      console.log('🔐 [AUTH_SLICE] Starting logout process...');
      
      // Call API logout with comprehensive cleanup
      await apiService.logout();
      
      console.log('✅ [AUTH_SLICE] Logout completed successfully');
      return { success: true, timestamp: new Date().toISOString() };
    } catch (error) {
      console.error('❌ [AUTH_SLICE] Logout error:', error);
      
      // Even if API logout fails, clear local state for security
      localStorage.clear();
      sessionStorage.clear();
      
      return rejectWithValue(error instanceof Error ? error.message : 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Verify auth
      .addCase(verifyAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = action.payload.authenticated;
        state.user = action.payload.user || null;
        state.error = null;
      })
      .addCase(verifyAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        // Don't set error for verification failures to prevent UI error loops
        state.error = null;
      })
      // Get profile
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      // Add user
      .addCase(addUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.error = null;
        state.isLoading = false;
        console.log('✅ [AUTH_SLICE] Auth state cleared after logout');
      })
      .addCase(logout.rejected, (state, action) => {
        // Even if logout fails, clear auth state for security
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.error = action.payload as string;
        state.isLoading = false;
        console.log('⚠️ [AUTH_SLICE] Auth state cleared after logout failure');
      });
  },
});

export const { clearError, setPermissions } = authSlice.actions;

// Export verifyAuth as checkAuthStatus for App.tsx compatibility
export const checkAuthStatus = verifyAuth;

export default authSlice.reducer;